import { CircleCheck, ShieldAlert, TriangleAlert, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { IconButton } from "@/components/ui/icon-button";

// O aviso que aparece depois de uma operação: "Organização criada", "Não foi
// possível desativar o usuário".
//
// Existia copiado em 14 telas — mesmo `<output>`, mesmo ícone, mesmo botão de
// fechar, sempre nove linhas de JSX. Copiar não é só verbosidade: cada cópia é
// uma chance de errar um detalhe que ninguém revisa. Foi o que aconteceu em
// Usuários, onde a mensagem de ERRO usava a moldura verde de sucesso — o
// usuário lia "não foi possível" pintado como se tivesse dado certo.
//
// Duas decisões de acessibilidade ficam presas ao tom, para não dependerem de
// quem copia lembrar delas:
//
// - `aria-live`: sucesso é `polite` (espera o leitor de tela terminar a frase),
//   erro é `assertive` (interrompe — a pessoa precisa saber que a ação falhou
//   antes de seguir clicando). ATENÇÃO é `polite`, e isso é a decisão que
//   define o tom: ele nasceu na TUT-171 para o aviso de nome repetido, em que a
//   ficha FOI gravada. Interromper ali ensinaria a ler todo aviso como falha, e
//   a pessoa desfaria um cadastro que deu certo.
// - o ícone: `CircleCheck`, `TriangleAlert` ou `ShieldAlert`. Cor sozinha não
//   comunica para quem não distingue verde de vermelho.

const toneClass = {
  success: cn(
    "border-[color-mix(in_srgb,var(--success)_50%,transparent)]",
    "bg-[var(--success-highlight)] text-[var(--success)]",
  ),
  // TUT-171: nem sucesso nem falha — "deu certo, e há algo para conferir".
  // O par `--warning` sobre `--warning-highlight` já é medido pelo
  // `contrast.test.ts`, então o tom nasce com contraste travado.
  warning: cn(
    "border-[color-mix(in_srgb,var(--warning)_50%,transparent)]",
    "bg-[var(--warning-highlight)] text-[var(--warning)]",
  ),
  error: cn(
    "border-[color-mix(in_srgb,var(--critical)_50%,transparent)]",
    "bg-[var(--critical-highlight)] text-[var(--critical)]",
  ),
} as const;

export type OperationFeedbackTone = keyof typeof toneClass;

// Só a moldura, para os avisos que não cabem em `message: string` — os de
// convite e de redefinição de senha carregam um link copiável dentro. Eles
// montam o conteúdo à mão, mas herdam a caixa; sem isso divergiriam do resto
// na primeira vez que alguém ajustasse o padding.
export function operationFeedbackClass(
  tone: OperationFeedbackTone = "success",
) {
  return cn(
    "mb-[0.85rem] grid grid-cols-[auto_1fr_auto] items-center gap-[0.65rem] rounded-lg border py-[0.55rem] pl-[0.85rem] pr-[0.7rem] text-legenda font-semibold",
    "[&>svg:not([class*='size-'])]:size-[1.05rem]",
    // O botão de fechar herda a cor do aviso em vez da própria. TUT-563b —
    // o fechar é o `IconButton` (`data-slot="icon-button"`), e não mais a
    // classe legada `.icon-button`: a mesma régua de 32 px e a cor do aviso.
    "[&_[data-slot=icon-button]]:size-8 [&_[data-slot=icon-button]]:text-current",
    toneClass[tone],
  );
}

export function OperationFeedback({
  tone = "success",
  message,
  onDismiss,
  className,
}: {
  tone?: OperationFeedbackTone;
  message: string;
  // Opcional: o aviso de senha alterada do Perfil some sozinho quando o
  // formulário é usado de novo, e um "x" ali seria botão sem função.
  onDismiss?: () => void;
  className?: string;
}) {
  if (!message) return null;

  const erro = tone === "error";
  const Icone =
    tone === "error"
      ? ShieldAlert
      : tone === "warning"
        ? TriangleAlert
        : CircleCheck;

  return (
    <output
      className={cn(operationFeedbackClass(tone), className)}
      aria-live={erro ? "assertive" : "polite"}
    >
      <Icone aria-hidden="true" />
      <span>{message}</span>
      {onDismiss ? (
        <IconButton
          label={
            tone === "success" ? "Fechar confirmação" : "Fechar aviso"
          }
          icone={<X aria-hidden="true" />}
          onClick={onDismiss}
        />
      ) : (
        // A grade tem três colunas fixas; sem isto o texto se espalharia pela
        // coluna do botão e o aviso ficaria com métrica diferente dos outros.
        <span />
      )}
    </output>
  );
}
